"use client";

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { GlassContainer } from '@/components/misc/glass-container';
import { cn, containerClasses } from '@/lib/utils';
import { DebugEditButton } from '@/components/mcp/debug/DebugEditButton';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8 }
  }
};

export function HeroCandidates() {
  const content = {
    title: "Deine Karriere. Unser Fokus.",
    description: "Entdecke umfassende Unterstützung bei jedem Schritt deines Bewerbungsprozesses – von der Stellensuche bis zum Interview. Wir machen deine Karriere einfacher, transparenter und erfolgreicher.",
    ctaText: "Kostenlos starten",
    ctaLink: "/contact?preselect=job",
    backgroundImage: "/hero-candidates.png"
  };

  const {
    title,
    description,
    ctaText,
    ctaLink,
    backgroundImage
  } = content;

  return (
    <section 
      className={cn(
        "relative h-[900px] overflow-hidden",
        "bg-white"
      )}
      aria-label="Hero Candidates section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 group">
        <Image
          src={backgroundImage}
          alt="Decorative background image"
          fill
          priority
          className="object-cover object-center"
          sizes="100vw"
          quality={90}
          aria-hidden="true"
        />
        
        {/* Debug edit button - only shown in debug mode and on hover */}
        <DebugEditButton 
          imageName={backgroundImage.split('/').pop()?.split('.')[0] || ''}
          imageUrl={backgroundImage}
        />
      </div>

      {/* Content Container */}
      <div className={cn(containerClasses, "h-full")}>
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="relative max-w-[660px]"
          >
            <GlassContainer intensity="medium">
              <h1 className={cn(
                "text-h2 mb-6",
                "whitespace-pre-line"
              )}>
                {title}
              </h1>

              <div className="text-body-lg">
                <div className={cn(
                  "mb-6",
                  "text-foreground/80"
                )}
                  aria-label="Hero description"
                >
                  {description}
                </div>
              </div>
            </GlassContainer>

            {/* Call to Action Button */}
            <motion.div 
              variants={fadeInUp}
              className={cn(
                "relative mt-8 sm:mt-10 z-20",
                "flex justify-center",
                "max-w-[330px] mx-auto"
              )}
            >
              <Link href={ctaLink} passHref>
                <Button 
                  variant="light-secondary"
                  size="lg"
                  className={cn(
                    "w-full"
                  )}
                >
                  {ctaText}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 